import { Field, useFormikContext, getIn } from 'formik';
import type { ActionMeta, GroupBase, Props, SingleValue } from 'react-select';
import { Select, SelectProps } from 'app/components/select';
import { ComponentStyles, StylesConfig } from 'app/components/select/Select.styles';
import { ReactNode, useCallback, useMemo } from 'react';

import { FormGroup } from '../form/Form';
import { FieldError } from './field-error';

type IProps<TDataItem extends Record<string, any>> = Omit<Props<TDataItem>, 'onChange'> & {
  name: string;
  label: string | ReactNode;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  data: TDataItem[];
  textField: string;
  valueField: string;
  hideValidationError?: boolean;
  isGrouped?: boolean;
  onChange?: (item: SingleValue<TDataItem>, actionMeta: ActionMeta<TDataItem>) => void;
};

const useSelectStyles = () => {
  return useCallback<ComponentStyles<Option, 'menu'>>(
    base => ({
      ...base,
      width: '100%',
    }),
    []
  );
};

const useFormikSelectStyles = (styles: SelectProps<Option>['styles']): StylesConfig<Option> => {
  const menu = useSelectStyles();
  return useMemo(
    () => ({
      ...styles,
      menu,
    }),
    [menu, styles]
  );
};

export type Option = {
  label: string;
  value: string;
};

export const FormikReactSelectControl = <TDataItem extends Record<string, any>>(
  props: IProps<TDataItem> & SelectProps<Option>
) => {
  const {
    name,
    label,
    hideValidationError,
    data,
    textField,
    valueField,
    required,
    disabled,
    onChange,
    styles,
    isGrouped,
    ...rest
  } = props;

  const combinedStyles = useFormikSelectStyles(styles);
  const { values, setFieldValue, touched, errors } = useFormikContext<Record<string, any>>();
  const formValue = getIn(values, name);
  const options = data.map(item => ({ label: item[textField], value: item[valueField] }));
  const groupedOptions = data.map(item => ({ label: item[textField], options: item[valueField] }));
  const selectedItem = options.find(item => item.value === formValue);
  return (
    <FormGroup
      label={label}
      labelFor={name}
      $isRequired={required}
      disabled={disabled}
      data-testid={`select-wrapper-${name}`}
    >
      <Field name={name}>
        {() => (
          <Select<Option, false, GroupBase<Option>>
            {...rest}
            isMulti={false}
            id={name}
            name={name}
            options={isGrouped ? groupedOptions : options}
            isDisabled={disabled}
            styles={combinedStyles}
            value={selectedItem}
            isError={!hideValidationError && !!getIn(touched, name) && getIn(errors, name)}
            onChange={(value, actionMeta) => {
              setFieldValue(name, value ? value.value : undefined);
              if (onChange) onChange(value, actionMeta);
            }}
          />
        )}
      </Field>
      {!hideValidationError && !!getIn(touched, name) && <FieldError name={name} />}
    </FormGroup>
  );
};
